import type { Session } from 'next-auth';

import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';

import {
    QUOTE_TYPES,
    type SapicContextualisedProductV5Payload,
    type SapicProductTypeV5,
    type SapicResponseTypeV5,
} from '@app-types/api';
import type { FaiProduct } from '@app-types/fai';
import type { PlanType } from '@app-types/product';

import { API_X_SOURCE } from '@constants/api';

import { SapicMapper } from 'src/mappers/sapic-mapper';

export const MAIN_OFFERS_CATEGORY = 'forfaits_mcommerce';
export const CATALOG_OFFERS_CATEGORY = 'catalogue_mcommerce';
export const FAI_BYOU_CATEGORY = 'fai_byou_mcommerce';

export const callSapicApi = async <T = PlanType>(
    payload?: SapicContextualisedProductV5Payload,
    session?: Session | null,
    category: string | null = MAIN_OFFERS_CATEGORY,
    filtre?: string,
    options?: Record<string, string>,
    xSource = API_X_SOURCE,
    mapper?: (product: SapicProductTypeV5) => T,
): Promise<T[]> => {
    if (!payload) {
        return Promise.resolve([]);
    }
    const headers: Record<string, string> = {
        'x-version': '5',
        'x-source': xSource,
    };
    const params = new URLSearchParams({
        clientCategorie: 'GP',
        modePourFinancement: 'auto',
        limite: '16',
        ...options,
    });

    if (category) {
        params.append('categorie', category);
    }

    if (filtre) {
        params.append('filtre', filtre);
    }

    const path = `${getAppConfig().bytelApi.url}/catalogue/produits-contextualises?${params}`;
    const result = await apiHttpService
        .post<SapicResponseTypeV5>(path, payload, {
            headers,
            user: session?.user,
            ttl: parseInt(getAppConfig().cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        })
        .then((result) => {
            if (mapper) {
                return result.produits.map(mapper);
            }
            return result.produits.map((product) => SapicMapper.mapToPlan(product, !!session?.user)) as T[];
        })
        .catch((error) => {
            console.error('[SAPIC] Error', error);
            return [];
        });

    return result;
};

export const getMobilePayload = (
    session: Session | null,
    offerGencode?: string,
): SapicContextualisedProductV5Payload => {
    const idPu = session?.user?.sub;
    const payload: SapicContextualisedProductV5Payload = {
        referenceExterneParcoursCible: 'idParcours-1',
        panier: {
            catalogue: 'WEB',
            parcours: [
                {
                    referenceExterne: 'idParcours-1',
                    typeParcours: QUOTE_TYPES.ACQUISITION,
                    elements: offerGencode
                        ? [
                              {
                                  referenceExterne: `idParcours-element-${offerGencode}`,
                                  type: 'ELEMENT_PANIER' as const,
                                  composantCommercial: {
                                      identifiant: offerGencode,
                                      typeCatalogue: 'BYTEL' as const,
                                  },
                              },
                          ]
                        : [],
                },
            ],
        },
    };

    if (idPu) {
        payload.panier.acheteur = {
            type: 'PERSONNE',
            noPersonne: idPu,
        };
    } else {
        payload.panier.acheteur = {
            type: 'NON_CONNU_GP',
        };
    }
    return payload;
};

export const getPayload = (
    session: Session | null,
    faiGencode?: string,
    isFai: boolean = false,
    singleFaiJourney: boolean = false,
    mobileGencode?: string,
): SapicContextualisedProductV5Payload => {
    const idPu = session?.user?.sub;
    const createProductElement = (gencode: string) => ({
        referenceExterne: `idParcours-element-${gencode}`,
        type: 'ELEMENT_PANIER' as const,
        composantCommercial: {
            identifiant: gencode,
            typeCatalogue: 'BYTEL' as const,
        },
    });

    const basePayload: SapicContextualisedProductV5Payload = {
        referenceExterneParcoursCible: 'idParcours-1',
        panier: {
            catalogue: 'WEB',
            parcours: [],
        },
    };

    if (idPu) {
        basePayload.panier.acheteur = {
            type: 'PERSONNE',
            noPersonne: idPu,
        };
    } else {
        basePayload.panier.acheteur = {
            type: 'NON_CONNU_GP',
        };
    }

    if (singleFaiJourney && faiGencode) {
        basePayload.panier.parcours = [
            {
                referenceExterne: 'idParcours-1',
                typeParcours: QUOTE_TYPES.ACQUISITION_FIXE,
                elements: [createProductElement(faiGencode)],
            },
        ];
        return basePayload;
    }

    if (mobileGencode) {
        basePayload.panier.parcours.push({
            referenceExterne: 'idParcours-1',
            typeParcours: QUOTE_TYPES.ACQUISITION,
            elements: [createProductElement(mobileGencode)],
        });
        return basePayload;
    }

    const mainJourney = {
        referenceExterne: 'idParcours-1',
        typeParcours: isFai ? QUOTE_TYPES.ACQUISITION_FIXE : QUOTE_TYPES.ACQUISITION,
        elements: [],
    };

    basePayload.panier.parcours = [mainJourney];

    if (faiGencode) {
        basePayload.panier.parcours.push({
            referenceExterne: 'idParcours-0',
            typeParcours: QUOTE_TYPES.ACQUISITION_FIXE,
            elements: [createProductElement(faiGencode)],
        });
    }

    return basePayload;
};

export const createRepeaterCall = (
    offerGencode: string | undefined,
    repeaterGencode: string | undefined,
    faiProducts: FaiProduct[],
    session: Session | null,
) =>
    offerGencode && repeaterGencode
        ? callSapicApi(
              getPayload(session, offerGencode, true, true),
              session,
              null,
              `gencodes in ("${repeaterGencode}")`,
              undefined,
              API_X_SOURCE,
              (apiData) => SapicMapper.mapToRepeater(apiData, faiProducts, !!session?.user),
          )
        : Promise.resolve([]);
